import axios from "axios";
import { useState } from "react";

import { API_URL } from "../constants";
import { authHeader } from "../utils/auth";

/**
 * Shared busy/error/notice state machine for the dashboard settings cards:
 * `save()` PATCHes `/auth/me/` with the caller's payload, hands the response
 * data to `onSaved`, and shows `successNote` — or the server's error message
 * (falling back to `errorFallback`) on failure. The caller keeps its own draft
 * state and builds the payload.
 *
 * @param {{
 *   buildPayload: () => object,
 *   onSaved: (data: any) => void,
 *   successNote: string,
 *   errorFallback: string,
 * }} options
 * @returns {{
 *   busy: boolean,
 *   error: string,
 *   notice: string,
 *   save: () => Promise<void>,
 *   clearNotice: () => void,
 *   setError: (message: string) => void,
 * }}
 */
const useAccountSettingsSave = ({ buildPayload, onSaved, successNote, errorFallback }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const clearNotice = () => setNotice("");

  const save = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const resp = await axios.patch(`${API_URL}/auth/me/`, buildPayload(), {
        headers: authHeader(),
      });
      onSaved(resp.data);
      setNotice(successNote);
    } catch (err) {
      setError(
        axios.isAxiosError(err) && typeof err.response?.data?.error === "string"
          ? err.response.data.error
          : errorFallback
      );
    } finally {
      setBusy(false);
    }
  };

  // `setError` is exposed for callers with pre-save validation (e.g. an image
  // that fails to load) so those errors share the same display slot.
  return { busy, error, notice, save, clearNotice, setError };
};

export default useAccountSettingsSave;
